import { z } from 'zod';
import { APP_KEYS } from '../rbac/primitives.js';
import { PLATFORM_ROLE_KEYS, WORKSPACE_ROLE_KEYS } from '../rbac/roles.js';
/**
 * Auth contracts, shared by the login form and the login route (ADR-0010).
 * Defined once here so a frontend/backend validation mismatch is a compile error.
 */
export const loginRequestSchema = z.object({
    email: z.string().trim().toLowerCase().email('Enter a valid email address.'),
    password: z.string().min(1, 'Enter your password.'),
});
/**
 * Password limits for the `local` strategy (dev and UAT only — production signs
 * in through DronaHQ, spec §2).
 *
 * The MINIMUM is master data (Rule 1): an Org Admin sets it per organisation.
 * These two bounds are genuinely code:
 *
 *  - The floor, 8, is NIST SP 800-63B's minimum for a memorised secret. An
 *    organisation may ask for more, never less — the same shape as Rule 4's
 *    bcrypt floor of 12.
 *  - The ceiling, 72 BYTES, is bcrypt's. Anything past it is silently ignored
 *    when hashing, so a longer password would accept every variant of its own
 *    tail. Refused rather than truncated, and counted in bytes because an
 *    Arabic character is two of them.
 */
export const PASSWORD_MIN_LENGTH_FLOOR = 8;
export const PASSWORD_MAX_BYTES = 72;
export function passwordByteLength(password) {
    return new TextEncoder().encode(password).length;
}
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    // The organisation's minimum is checked by the service, which knows it.
    newPassword: z
        .string()
        .min(1, 'Enter a new password.')
        .refine((value) => passwordByteLength(value) <= PASSWORD_MAX_BYTES, {
        message: `A password can be at most ${PASSWORD_MAX_BYTES} bytes long.`,
    }),
});
/**
 * The DronaHQ container hands us a profile and nothing more (ADR-0004).
 * Identity sync is username + project only — no roles cross this boundary, ever.
 */
export const ssoProfileSchema = z.object({
    username: z.string().trim().min(1),
    email: z.string().trim().toLowerCase().email().optional(),
    displayName: z.string().trim().optional(),
    /** External project code from the platform, matched against our projects master. */
    projectCode: z.string().trim().optional(),
});
export const roleScopeSchema = z.object({
    businessUnitIds: z.array(z.string().uuid()).default([]),
    projectIds: z.array(z.string().uuid()).default([]),
});
export const workspaceRoleAssignmentSchema = z.object({
    app: z.enum(APP_KEYS),
    roleKey: z.enum(WORKSPACE_ROLE_KEYS).nullable(),
    scope: roleScopeSchema,
    expiresAt: z.string().datetime().nullable().optional(),
});
/**
 * The session payload. Deliberately identical whichever strategy authenticated the
 * request — nothing downstream can tell a local login from a container hand-off.
 */
export const sessionUserSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    username: z.string(),
    email: z.string().email(),
    displayName: z.string(),
    designation: z.string().nullable(),
    platformRole: z.enum(PLATFORM_ROLE_KEYS).nullable(),
    workspaceRoles: z.array(workspaceRoleAssignmentSchema),
    homeScope: roleScopeSchema,
    locale: z.enum(['en', 'ar']),
    /** Which strategy this account signs in with; only `local` has a password here. */
    authSource: z.enum(['local', 'container']),
    /**
     * True after an administrator reset the password. Until it is changed, the
     * API refuses everything except changing it, signing out and reading /me.
     */
    passwordChangeRequired: z.boolean(),
});
/** What sign-in and a token refresh answer (pass 9: this said `token`). */
export const loginResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    user: sessionUserSchema,
});
// ---------------------------------------------------------------------------
// Production's first-run setup (POST /setup)
// ---------------------------------------------------------------------------
/** An IANA timezone the runtime knows, e.g. "Asia/Riyadh". */
const timezone = z
    .string()
    .trim()
    .min(1)
    .refine((value) => {
    try {
        new Intl.DateTimeFormat('en', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}, { message: 'Use a timezone name such as Asia/Riyadh.' });
export const firstRunSetupSchema = z.object({
    token: z.string().min(1).max(512),
    organisation: z.object({
        name: z.string().trim().min(2).max(160),
        defaultLocale: z.enum(['en', 'ar']).default('en'),
        timezone,
        /** ISO weekdays, 1 = Monday … 7 = Sunday. Never assumed (Rule 1). */
        workingWeekdays: z
            .array(z.number().int().min(1).max(7))
            .min(1, 'A working week needs at least one working day.')
            .max(7)
            .refine((days) => new Set(days).size === days.length, { message: 'A weekday is listed twice.' }),
    }),
    administrator: z.object({
        username: z
            .string()
            .trim()
            .min(2)
            .max(80)
            .regex(/^[a-z0-9._-]+$/i, 'Letters, numbers, dots, dashes and underscores only.'),
        email: z.string().trim().toLowerCase().email(),
        displayName: z.string().trim().min(1).max(160),
        password: z
            .string()
            .min(1)
            .refine((value) => passwordByteLength(value) <= PASSWORD_MAX_BYTES, {
            message: `A password can be at most ${PASSWORD_MAX_BYTES} bytes long.`,
        }),
    }),
});
//# sourceMappingURL=auth.js.map